// GYM-1592: AppShell with skip-to-content link + focus management on route change.
import { Component, Suspense, useEffect, useRef } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { FaqDock } from "@/components/faq/FaqDock";
import { InstallPrompt } from "@/components/pwa/InstallPrompt";
import { MobileBottomNav } from "./MobileBottomNav";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import { useIsMobile } from "@/lib/hooks/useIsMobile";
import { cn } from "@/lib/utils/cn";

// Catches dynamic-import (chunk load) failures caused by stale browser cache
// after a new deployment. Reloads once to get fresh chunk hashes.
class ChunkErrorBoundary extends Component<{ children: ReactNode }, { errored: boolean }> {
  state = { errored: false };

  static getDerivedStateFromError() {
    return { errored: true };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    const isChunkError =
      error.message.includes("dynamically imported module") ||
      error.message.includes("Failed to fetch dynamically") ||
      error.name === "ChunkLoadError";
    if (isChunkError && !sessionStorage.getItem("chunk_reload")) {
      sessionStorage.setItem("chunk_reload", "1");
      window.location.reload();
    }
  }

  render() {
    if (this.state.errored) {
      return (
        <div className="flex h-full min-h-[60vh] flex-col items-center justify-center gap-3 text-center">
          <p className="text-sm text-ink-600">Page failed to load. Please refresh.</p>
          <button
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
            onClick={() => window.location.reload()}
          >
            Refresh
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// True when the app is rendered inside an iframe (e.g. the Getting Started
// guide popup): the chrome is hidden so only the page content shows.
const IS_EMBEDDED = typeof window !== "undefined" && window.self !== window.top;

export function AppShell() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();

  // Move keyboard focus to the main landmark on every navigation so
  // screen-reader users don't have to tab through the entire header again.
  // On phones also start each screen at the top, like a native page push;
  // desktop keeps its current scroll behaviour.
  useEffect(() => {
    mainRef.current?.focus();
    if (isMobile) window.scrollTo({ top: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run on navigation only
  }, [pathname]);

  return (
    <div className="flex min-h-dvh">
      {/* Skip-to-content link — visible only on keyboard focus */}
      <a
        href="#main-content"
        className={cn(
          "sr-only focus:not-sr-only",
          "fixed top-2 start-2 z-[100] rounded-sm bg-primary-600 px-4 py-2 text-sm font-semibold text-white",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:ring-offset-2",
        )}
      >
        {t("nav.skipToContent")}
      </a>

      {/* Desktop sidebar — hidden on mobile */}
      {!IS_EMBEDDED && <Sidebar />}

      <div className="flex min-w-0 flex-1 flex-col min-h-dvh">
        {!IS_EMBEDDED && <TopBar />}
        {/* pb-20 adds space for the mobile bottom nav (56px + 16px buffer) */}
        <main
          id="main-content"
          ref={mainRef}
          tabIndex={-1}
          className="min-w-0 flex-1 px-4 sm:px-6 lg:px-8 py-6 pb-20 md:pb-6 max-w-[1440px] w-full mx-auto focus-visible:outline-none"
        >
          <ChunkErrorBoundary>
            <Suspense fallback={
              <div className="flex h-full min-h-[60vh] items-center justify-center">
                <svg className="h-8 w-8 animate-spin text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              </div>
            }>
              <Outlet />
            </Suspense>
          </ChunkErrorBoundary>
        </main>
      </div>

      {/* Mobile bottom navigation — hidden on desktop */}
      {!IS_EMBEDDED && <MobileBottomNav />}

      {/* Global FAQ popup — stays open across module navigation */}
      {!IS_EMBEDDED && <FaqDock />}

      {/* PWA install prompt — mobile only */}
      {!IS_EMBEDDED && <InstallPrompt />}
    </div>
  );
}
